'use client';

import { createTheme, ThemeOptions } from '@mui/material';
import { PinnacleThemes } from './types';

const DEFAULT_FONT_FAMILY = '"Inter", sans-serif';

const themes = new Map<PinnacleThemes, ThemeOptions>();
themes.set(PinnacleThemes.MUI_DEFAULT, {});

themes.set(PinnacleThemes.BASE_THEME, {
    typography: {
        allVariants: {
            // color: '#fff',
        },
        h1: {
            fontSize: '2rem',
            fontWeight: 300,
        },
        h2: {
            fontSize: '1.5rem',
            fontWeight: 300,
        },
        h3: {
            fontSize: '1.25rem',
            fontWeight: 400,
        },
        h4: {
            fontSize: '1.125rem',
            fontWeight: 400,
        },
        h5: {
            fontSize: '1rem',
            fontWeight: 400,
        },
        h6: {
            fontSize: '0.875rem',
            fontWeight: 500,
        },
        subtitle1: {
            fontSize: '0.875rem',
            fontWeight: 400,
        },
        subtitle2: {
            fontSize: '0.75rem',
            fontWeight: 500,
        },
        body1: {
            fontSize: '1rem',
            fontWeight: 300,
        },
        body2: {
            fontSize: '0.875rem',
            fontWeight: 400,
        },
        button: {
            fontSize: '0.875rem',
            fontWeight: 400,
            color: 'rgba(var(--rgb-secondary))',
        },
        caption: {
            fontSize: '0.75rem',
            fontWeight: 400,
        },
        overline: {
            fontSize: '0.625rem',
            fontWeight: 400,
        },
    },
    palette: {
        primary: {
            main: 'rgba(var(--rgb-primary))',
            light: 'rgba(var(--rgb-primary), 0.15)',
        },
        secondary: {
            main: 'rgba(var(--rgb-secondary), 1)',
            light: 'rgba(var(--rgb-secondary), 1)',
        },
        common: {
            white: '#ffffff',
            black: '#000000',
        },
        disabled: {
            icon: '#777777',
            text: '#777777',
            background: '#C8C8C8',
        },
        success: {
            main: 'rgba(var(--rgb-success))',
            light: 'rgba(var(--rgb-success), 0.15)',
        },
        error: {
            main: 'rgba(var(--rgb-error))',
            light: 'rgba(var(--rgb-error), 0.15)',
        },
        info: {
            main: 'rgba(var(--rgb-info))',
            light: 'rgba(var(--rgb-info), 0.15)',
        },
    },
    components: {
        MuiAlertTitle: {
            styleOverrides: {
                root: {
                    color: 'inherit',
                },
            },
        },
        MuiSnackbarContent: {
            styleOverrides: {
                root: {
                    color: '#fff',
                },
            },
        },
        MuiSkeleton: {
            styleOverrides: {
                root: {
                    backgroundColor: 'var(--skeleton-bg)',
                },
            },
        },
        MuiTypography: {
            defaultProps: {
                textTransform: 'none',
            },
            styleOverrides: {
                root: {
                    wordBreak: 'break-word',
                    color: "#fff",
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    color: 'inherit',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 400,
                    color: "black",
                },
            },
            defaultProps: {
                color: 'secondary',
            },
            variants: [
                {
                    props: { variant: 'contained', color: 'secondary' },
                    style: {
                        backgroundColor: 'rgba(var(--rgb-secondary))',
                        '&:hover': {
                            backgroundColor: 'rgba(var(--rgb-secondary), 0.9)',
                        },
                    },
                },
                {
                    props: { variant: 'outlined', color: 'secondary' },
                    style: {
                        color: 'rgba(var(--rgb-secondary))',
                        border: '1px solid rgba(var(--rgb-secondary), 0.6)',
                        '&:hover': {
                            textDecoration: 'none',
                            backgroundColor: 'rgba(var(--rgb-secondary), 0.04)',
                            border: '1px solid rgb(var(--rgb-secondary))',
                        },
                    },
                },
                {
                    props: { variant: 'text', color: 'primary' },
                    style: {
                        color: 'rgba(var(--rgb-primary))',
                        '&:hover': {
                            backgroundColor: 'rgba(var(--rgb-primary), 0.04)',
                        },
                    },
                },
                {
                    props: { variant: 'text', color: 'secondary' },
                    style: {
                        color: 'rgba(var(--rgb-secondary))',
                        '&:hover': {
                            backgroundColor: 'rgba(var(--rgb-secondary), 0.04)',
                        },
                    },
                },
            ],
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    backgroundColor: "#222222",
                    color: "#fff",
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    "&:hover": {
                        backgroundColor: "#333333",
                    },
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    fontFamily: DEFAULT_FONT_FAMILY,
                },
            },
        },
        MuiTextField: {
            defaultProps: {
                SelectProps: {
                    MenuProps: {
                        /**
                         * This fixes a default styling issue in MUI where TextField select
                         * dropdowns can get misaligned with their input fields on narrower
                         * screensizes if the dropdown is not at least 16px from the screen
                         * edges. Setting marginThreshold to 0 will prevent unnecessary
                         * edge-offsetting behaviour for TextFields dropdowns.
                         *
                         * @see {@link https://github.com/mui/material-ui/issues/35155}
                         */
                        marginThreshold: 0,
                    },
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    fontSize: 11,
                },
            },
        },
        MuiIcon: {
            variants: [
                {
                    props: {
                        color: 'primary',
                    },
                    style: {
                        color: 'rgba(var(--rgb-primary))',
                    },
                },
                {
                    props: {
                        color: 'secondary',
                    },
                    style: {
                        color: 'rgba(var(--rgb-secondary))',
                    },
                },
            ],
        },
        MuiChip: {
            variants: [
                {
                    props: { variant: 'outlined', color: 'primary' },
                    style: {
                        border: '1px solid rgba(var(--rgb-primary))',
                    },
                },
                {
                    props: { variant: 'outlined', color: 'secondary' },
                    style: {
                        border: '1px solid rgba(var(--rgb-secondary))',
                    },
                },
            ],
        },
        MuiAutocomplete: {
            styleOverrides: {
                listbox: ({ theme }) => ({
                    '.MuiAutocomplete-option[aria-selected="true"]': {
                        backgroundColor: theme.palette.secondary.light,
                    },
                }),
            },
        },
    },
});

export const getTheme = (themeName: PinnacleThemes) =>
    createTheme(
        {
            typography: { fontFamily: DEFAULT_FONT_FAMILY },
        },
        themes.get(themeName) || {},
    );

export default getTheme;
